import { useCallback } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export enum SortId {
  ModificationDate = 'modificationDate',
  Name = 'name',
  CreationDate = 'creationDate',
  TrashingDate = 'trashingDate',
  State = 'state',
}

export type TableType = 'vault' | 'trash' | 'byYou' | 'vaultTransfer';

export type SortSetting = {
  sorting: {
    id: SortId;
    desc: boolean;
  };
};

interface DocumentSortingStore {
  vault: SortSetting;
  trash: SortSetting;
  fromContacts: SortSetting;
  byYou: SortSetting;
  vaultTransfer: SortSetting;
  setActiveSorting: (activeSorting: Partial<SortSetting['sorting']>, tableName: TableType) => void;
}

const initialState: DocumentSortingStore = {
  vault: {
    sorting: {
      id: SortId.ModificationDate,
      desc: true,
    },
  },
  trash: {
    sorting: {
      id: SortId.TrashingDate,
      desc: true,
    },
  },
  fromContacts: {
    sorting: {
      id: SortId.State,
      desc: true,
    },
  },
  byYou: {
    sorting: {
      id: SortId.Name,
      desc: true,
    },
  },
  vaultTransfer: {
    sorting: {
      id: SortId.ModificationDate,
      desc: true,
    },
  },
  setActiveSorting: () => {},
} as const;

const useDocumentSortingStore = create<DocumentSortingStore>()(
  persist(
    (set) => ({
      ...initialState,
      setActiveSorting: (activeSorting, tableType) =>
        set((state) => ({
          ...state,
          [tableType]: {
            sorting: {
              ...state[tableType].sorting,
              ...activeSorting,
            },
          },
        })),
    }),
    {
      name: 'documentSortingStore',
      version: 3,
    },
  ),
);

export const useDocumentSorting = (tableType: TableType) => {
  const state = useDocumentSortingStore();

  const { sorting } = state[tableType];
  const handleSetActiveSorting = useCallback(
    (sortingValues: Partial<SortSetting['sorting']>) => {
      return state.setActiveSorting(sortingValues, tableType);
    },
    [state, tableType],
  );

  return {
    activeSorting: sorting,
    setActiveSorting: handleSetActiveSorting,
  };
};
